import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  ArrowRight, Clock, Eye, Share2, Check, List, User2,
} from 'lucide-react'
import { Shell } from '../components/Layout'
import { Card, Rows } from '../components/UI'
import Markdown, { tocOf } from '../components/Markdown'
import { get } from '../lib/api'
import { faDate, timeAgo, asArray, num } from '../lib/format'
import { useSeo } from '../lib/seo'

function ReadingBar() {
  const [pct, setPct] = useState(0)
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight
      setPct(h > 0 ? Math.min(100, Math.max(0, (window.scrollY / h) * 100)) : 0)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  return (
    <div className="fixed inset-x-0 top-0 z-50 h-0.5 bg-transparent">
      <div className="h-full bg-gradient-to-l from-brand-500 to-aqua transition-[width] duration-150"
           style={{ width: `${pct}%` }} />
    </div>
  )
}

function Share({ title }: { title: string }) {
  const [copied, setCopied] = useState(false)
  const url = typeof window !== 'undefined' ? window.location.href : ''

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch {
      setCopied(false)
    }
  }

  const links = [
    { label: 'تلگرام', href: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}` },
    { label: 'واتساپ', href: `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}` },
    { label: 'ایکس', href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}` },
  ]

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-1 text-xs text-slate-500">
        <Share2 className="h-3.5 w-3.5" /> اشتراک‌گذاری:
      </span>
      {links.map((l) => (
        <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer nofollow"
           className="chip !py-1 text-[11px] text-slate-300 hover:text-white">
          {l.label}
        </a>
      ))}
      <button onClick={copy} className="chip !py-1 text-[11px] text-slate-300 hover:text-white">
        {copied ? <><Check className="h-3 w-3 text-up" /> کپی شد</> : 'کپی لینک'}
      </button>
    </div>
  )
}

function Toc({ items }: { items: { level: number; text: string; id: string }[] }) {
  const [active, setActive] = useState('')
  useEffect(() => {
    if (!items.length || typeof IntersectionObserver === 'undefined') return
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length) setActive(visible[0].target.id)
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 },
    )
    items.forEach((h) => {
      const el = document.getElementById(h.id)
      if (el) obs.observe(el)
    })
    return () => obs.disconnect()
  }, [items])

  if (items.length < 2) return null
  return (
    <Card className="lg:sticky lg:top-24">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-bold text-white">
        <List className="h-4 w-4 text-brand-300" /> فهرست مطالب
      </h2>
      <nav>
        <ul className="space-y-1.5">
          {items.map((h) => (
            <li key={h.id} className={h.level === 3 ? 'pr-3' : ''}>
              <a
                href={`#${h.id}`}
                onClick={(e) => {
                  e.preventDefault()
                  const el = document.getElementById(h.id)
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    history.replaceState(null, '', `#${h.id}`)
                  }
                }}
                className={`block border-r-2 pr-2 text-xs leading-6 transition ${
                  active === h.id
                    ? 'border-brand-400 text-brand-200'
                    : 'border-white/10 text-slate-400 hover:text-slate-200'
                }`}
              >
                {h.text}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </Card>
  )
}

function Faq({ items }: { items: any[] }) {
  if (!items.length) return null
  return (
    <section className="mt-10">
      <h2 className="mb-4 text-lg font-extrabold text-white">پرسش‌های متداول</h2>
      <div className="space-y-2">
        {items.map((f: any, i: number) => (
          <details key={i} className="rounded-2xl border border-white/10 bg-white/[.03] p-4">
            <summary className="cursor-pointer text-sm font-bold text-slate-200">{f.q}</summary>
            <p className="mt-3 text-sm leading-8 text-slate-400">{f.a}</p>
          </details>
        ))}
      </div>
    </section>
  )
}

function MiniCard({ post, label }: { post: any; label: string }) {
  return (
    <Link to={`/blog/${post.slug}`}>
      <Card hover className="h-full">
        <span className="text-[11px] text-slate-500">{label}</span>
        <p className="mt-1.5 text-sm font-bold leading-7 text-white">{post.title}</p>
      </Card>
    </Link>
  )
}

export default function BlogPost() {
  const { slug } = useParams()
  const [post, setPost] = useState<any>(null)
  const [notFound, setNotFound] = useState(false)
  // پیکربندی هاب نشریار — نمایش نویسنده/تاریخ/بازدید را کنترل می‌کند
  const [cfg, setCfg] = useState<any>({})

  useEffect(() => {
    get('/content/blog-config')
      .then((r) => setCfg(r && typeof r === 'object' && !Array.isArray(r) ? r : {}))
      .catch(() => setCfg({}))
  }, [])

  useEffect(() => {
    let alive = true
    setPost(null)
    setNotFound(false)
    get(`/content/posts/${encodeURIComponent(String(slug))}`)
      .then((r) => { if (alive) setPost(r) })
      .catch(() => { if (alive) setNotFound(true) })
    return () => { alive = false }
  }, [slug])

  // فهرست مطالب از بک‌اند می‌آید؛ اگر نیامد، همان‌جا از متن ساخته می‌شود تا
  // ستون کناری خالی نماند.
  const toc = useMemo(
    () => (asArray<any>(post?.toc).length ? asArray<any>(post.toc) : tocOf(post?.body || '')),
    [post],
  )

  useSeo(
    post
      ? {
          title: post.seo?.title || post.title,
          description: post.seo?.description || post.excerpt,
          keywords: post.seo?.keywords,
          canonical: post.seo?.canonical || `/blog/${post.slug}`,
          // تصویر پیش‌فرض هاب، آخرین سنگر است تا هیچ اشتراک‌گذاری بی‌تصویر نماند
          image: post.seo?.image || post.coverUrl || cfg?.defaultOgImage,
          imageAlt: post.seo?.imageAlt || post.coverAlt || post.title,
          noindex: !!post.seo?.noindex,
          // ناشر می‌تواند کل وبلاگ را از ایندکس بیرون ببرد؛ لینک‌ها دنبال می‌شوند
          noindexFollow: cfg?.blogNoindex === true,
          type: 'article' as const,
          publishedTime: post.publishedAt,
          modifiedTime: post.updatedAt,
          author: post.author,
          section: post.category,
          // بک‌اند وقتی `structuredData` خاموش باشد آرایه خالی می‌دهد؛ این
          // بررسی برای پاسخ‌های کش‌شده یا قدیمی است.
          jsonLd: cfg?.structuredData === false ? null : post.jsonLd,
          titleTemplate: cfg?.titleTemplate,
          twitterSite: cfg?.twitterHandle,
          googleVerification: cfg?.googleVerification,
        }
      : notFound
        ? { title: 'مطلب یافت نشد', noindex: true }
        : null,
    [post?.slug, notFound, cfg],
  )

  const related = asArray<any>(post?.related)
  const faq = asArray<any>(post?.faq)
  const tags = asArray<string>(post?.tags)

  return (
    <Shell>
      {post && <ReadingBar />}

      {/* مسیر راهنما — هم برای کاربر و هم برای گوگل */}
      <nav aria-label="مسیر" className="mb-5 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
        <Link to="/" className="hover:text-slate-300">خانه</Link>
        <span>/</span>
        <Link to="/blog" className="hover:text-slate-300">وبلاگ</Link>
        {post?.category && (
          <>
            <span>/</span>
            <Link to={`/blog?category=${encodeURIComponent(post.category)}`} className="hover:text-slate-300">
              {post.category}
            </Link>
          </>
        )}
      </nav>

      {notFound ? (
        <Card>
          <div className="py-12 text-center">
            <p className="mb-4 text-sm text-slate-400">مطلب مورد نظر یافت نشد یا هنوز منتشر نشده است.</p>
            <Link to="/blog" className="btn-primary !px-5 text-sm">بازگشت به وبلاگ</Link>
          </div>
        </Card>
      ) : !post ? (
        <Rows n={6} h="h-8" />
      ) : (
        <div className="grid gap-8 lg:grid-cols-[1fr,17rem]">
          <article className="min-w-0">
            {post.coverUrl ? (
              <img src={post.coverUrl} alt={post.coverAlt || post.title}
                   className="mb-6 max-h-80 w-full rounded-2xl border border-white/10 object-cover" />
            ) : (
              <div className="mb-6 h-48 rounded-2xl bg-gradient-to-br from-brand-600/45 via-brand-500/10 to-aqua/25" />
            )}

            <h1 className="mb-4 text-2xl font-extrabold leading-[1.7] text-white sm:text-3xl">
              {post.title}
            </h1>

            <div className="mb-6 flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
              <span className="chip !py-0.5 text-[11px] text-brand-200">{post.category}</span>
              {cfg?.showAuthor !== false && (
                <span className="inline-flex items-center gap-1"><User2 className="h-3 w-3" /> {post.author}</span>
              )}
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" /> {num(post.readingMinutes || 1)} دقیقه مطالعه
              </span>
              {cfg?.showViews !== false && post.views > 0 && (
                <span className="inline-flex items-center gap-1">
                  <Eye className="h-3 w-3" /> {num(post.views)} بازدید
                </span>
              )}
              {cfg?.showDate !== false && (
                <>
                  <time dateTime={post.publishedAt || undefined}>{faDate(post.publishedAt, false)}</time>
                  {post.updatedAt && post.updatedAt !== post.publishedAt && (
                    <span className="text-slate-600">به‌روزرسانی {timeAgo(post.updatedAt)}</span>
                  )}
                </>
              )}
            </div>

            {post.excerpt && (
              <p className="mb-7 rounded-2xl border-r-2 border-brand-500/50 bg-white/[.03] p-4 text-sm leading-8 text-slate-300">
                {post.excerpt}
              </p>
            )}

            <div className="blog-body">
              <Markdown body={post.body || ''} />
            </div>

            {tags.length > 0 && (
              <div className="mt-8 flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-slate-500">برچسب‌ها:</span>
                {tags.map((t) => (
                  <Link key={t} to={`/blog?tag=${encodeURIComponent(t)}`}
                        className="chip !py-1 text-[11px] text-slate-400 hover:text-slate-200">
                    {t}
                  </Link>
                ))}
              </div>
            )}

            <div className="mt-6 border-t border-white/10 pt-5">
              <Share title={post.title} />
            </div>

            <Faq items={faq} />

            {(post.prev || post.next) && (
              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                {post.prev && <MiniCard post={post.prev} label="مطلب قبلی" />}
                {post.next && <MiniCard post={post.next} label="مطلب بعدی" />}
              </div>
            )}

            {related.length > 0 && (
              <section className="mt-10">
                <h2 className="mb-4 text-lg font-extrabold text-white">مطالب مرتبط</h2>
                <div className="grid gap-4 sm:grid-cols-3">
                  {related.map((r: any) => (
                    <Link key={r.slug} to={`/blog/${r.slug}`}>
                      <Card hover className="h-full">
                        <span className="chip !py-0.5 text-[10px] text-brand-200">{r.category}</span>
                        <p className="mt-2 text-sm font-bold leading-7 text-white">{r.title}</p>
                        <p className="mt-1 text-xs leading-6 text-slate-500">
                          {num(r.readingMinutes || 1)} دقیقه · {timeAgo(r.publishedAt)}
                        </p>
                      </Card>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>

          <aside className="space-y-5">
            <Toc items={toc} />
            <Card>
              <h2 className="mb-2 text-sm font-bold text-white">شروع معامله در ارزینو</h2>
              <p className="mb-4 text-xs leading-7 text-slate-400">
                خرید و فروش بیش از ۲۰ ارز دیجیتال با کمترین کارمزد و واریز آنی.
              </p>
              <Link to="/register" className="btn-primary w-full justify-center text-sm">ثبت‌نام رایگان</Link>
              <Link to="/markets" className="btn-ghost mt-2 w-full justify-center text-xs">مشاهده بازارها</Link>
            </Card>
          </aside>
        </div>
      )}

      <Link to="/blog" className="btn-ghost mt-8 !px-3 text-xs">
        <ArrowRight className="h-4 w-4" /> بازگشت به وبلاگ
      </Link>
    </Shell>
  )
}
